/* School calendar tab (iter74) — configure academic year, holidays,
 * public holidays, and work days. Used inside /settings. */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DatePicker, DateRangePicker } from '../ui/date-picker';
import { CalendarBlank, Plus, Trash, FloppyDisk } from '@phosphor-icons/react';
import api from '../../lib/api';

const KIND_OPTIONS = [
  { value: 'toussaint', label: 'Toussaint' },
  { value: 'noel', label: 'Noël' },
  { value: 'fevrier', label: 'Février' },
  { value: 'paques', label: 'Pâques' },
  { value: 'vacances', label: 'Grandes Vacances' },
];

const WEEKDAYS = [
  { v: 0, l: 'Lundi' }, { v: 1, l: 'Mardi' }, { v: 2, l: 'Mercredi' },
  { v: 3, l: 'Jeudi' }, { v: 4, l: 'Vendredi' },
  { v: 5, l: 'Samedi' }, { v: 6, l: 'Dimanche' },
];

export default function SchoolCalendarTab({ schoolYears = [] }) {
  const activeYear = schoolYears.find((y) => y.is_active)?.name
    || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;
  const [year, setYear] = useState(activeYear);
  const [cal, setCal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [fetchCountry, setFetchCountry] = useState('CI');
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeStatus, setRecomputeStatus] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/school-calendar?year=${year}`);
      setCal(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    }
  };
  useEffect(() => { load(); }, [year]); // eslint-disable-line

  // iter81 — Auto-detect country from school_config
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/school/config');
        const cc = (data?.country_code || data?.country || '').toUpperCase();
        if (cc) setFetchCountry(cc);
      } catch (e) { /* keep default */ }
    })();
  }, []);

  // iter79 — poll recompute status
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled || !year) return;
      try {
        const { data } = await api.get(
          `/school-calendar/recompute-status?academic_year=${encodeURIComponent(year)}`,
        );
        if (!cancelled) setRecomputeStatus(data);
      } catch (e) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [year]);

  const fetchOfficial = async () => {
    setFetching(true);
    try {
      const { data } = await api.post(
        `/school-calendar/fetch-public-holidays?academic_year=${encodeURIComponent(cal.academic_year)}&country=${encodeURIComponent(fetchCountry)}`,
      );
      toast.success(
        `${data.country} ${data.years.join('/')} — ${data.added} ajout(s), ${data.updated} mise(s) à jour`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Source publique inaccessible');
    } finally { setFetching(false); }
  };

  const COUNTRY_OPTIONS = [
    { code: 'CI', label: '🇨🇮 Côte d\'Ivoire' },
    { code: 'FR', label: '🇫🇷 France' },
    { code: 'SN', label: '🇸🇳 Sénégal' },
    { code: 'ML', label: '🇲🇱 Mali' },
    { code: 'BF', label: '🇧🇫 Burkina Faso' },
    { code: 'BJ', label: '🇧🇯 Bénin' },
    { code: 'TG', label: '🇹🇬 Togo' },
    { code: 'CM', label: '🇨🇲 Cameroun' },
    { code: 'GA', label: '🇬🇦 Gabon' },
    { code: 'CD', label: '🇨🇩 RD Congo' },
    { code: 'MA', label: '🇲🇦 Maroc' },
    { code: 'DZ', label: '🇩🇿 Algérie' },
    { code: 'TN', label: '🇹🇳 Tunisie' },
    { code: 'BE', label: '🇧🇪 Belgique' },
    { code: 'CH', label: '🇨🇭 Suisse' },
    { code: 'CA', label: '🇨🇦 Canada' },
    { code: 'US', label: '🇺🇸 États-Unis' },
    { code: 'GB', label: '🇬🇧 Royaume-Uni' },
  ];

  if (!cal) return <p className="text-sm text-[#94A3B8] italic">Chargement…</p>;

  const set = (k, v) => setCal(prev => ({ ...prev, [k]: v }));
  const setHoliday = (i, k, v) => setCal(prev => ({
    ...prev,
    holidays: prev.holidays.map((h, j) => (j === i ? { ...h, [k]: v } : h)),
  }));
  const setPh = (i, k, v) => setCal(prev => ({
    ...prev,
    public_holidays: prev.public_holidays.map((p, j) => (j === i ? { ...p, [k]: v } : p)),
  }));

  const addHoliday = () => set('holidays', [
    ...cal.holidays,
    { kind: 'toussaint', label: '', start_date: '', end_date: '' },
  ]);
  const removeHoliday = (i) => set('holidays',
    cal.holidays.filter((_, j) => j !== i));

  const addPh = () => set('public_holidays', [
    ...cal.public_holidays, { date: '', label: '' },
  ]);
  const removePh = (i) => set('public_holidays',
    cal.public_holidays.filter((_, j) => j !== i));

  const toggleWorkDay = (v) => {
    const cur = new Set(cal.work_days || []);
    if (cur.has(v)) cur.delete(v); else cur.add(v);
    set('work_days', Array.from(cur).sort());
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/school-calendar', {
        academic_year: cal.academic_year,
        year_start_date: cal.year_start_date || '',
        year_end_date: cal.year_end_date || '',
        work_days: cal.work_days || [0, 1, 2, 3, 4],
        holidays: cal.holidays || [],
        public_holidays: cal.public_holidays || [],
      });
      setCal(data);
      toast.success('Calendrier enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="school-calendar-tab">
      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center gap-2 flex-wrap justify-between">
            <div className="flex items-center gap-2 font-outfit font-semibold text-[#0F172A]">
              <CalendarBlank size={22} className="text-[#0052FF]" weight="duotone" />
              Calendrier scolaire — Périodes, congés et jours ouvrés
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-[#64748B]">Année scolaire</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-40 h-9" data-testid="cal-year-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(schoolYears || []).map((y) => (
                    <SelectItem key={y.name} value={y.name}>{y.name}</SelectItem>
                  ))}
                  {!schoolYears.find((y) => y.name === year) && (
                    <SelectItem value={year}>{year}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Button
                onClick={save} disabled={saving}
                className="bg-[#0052FF] hover:bg-[#0043D1]"
                data-testid="cal-save-btn"
              >
                <FloppyDisk size={14} className="mr-1" />
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </div>

          {/* Year dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="font-manrope text-xs">Rentrée scolaire</Label>
              <DatePicker
                value={cal.year_start_date || ''}
                onChange={(v) => set('year_start_date', v)}
                testid="cal-year-start"
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <Label className="font-manrope text-xs">Fin d'année scolaire</Label>
              <DatePicker
                value={cal.year_end_date || ''}
                onChange={(v) => set('year_end_date', v)}
                testid="cal-year-end"
                className="w-full"
              />
            </div>
          </div>

          {/* Work days */}
          <div className="space-y-2">
            <Label className="font-manrope text-xs">Jours ouvrés de cours</Label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map((w) => {
                const active = (cal.work_days || []).includes(w.v);
                return (
                  <button
                    key={w.v} type="button"
                    onClick={() => toggleWorkDay(w.v)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      active
                        ? 'bg-[#0052FF] text-white border-[#0052FF]'
                        : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0052FF]'
                    }`}
                    data-testid={`cal-workday-${w.v}`}
                  >
                    {w.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Named holidays */}
          <div className="space-y-2 border-t border-[#E2E8F0] pt-4">
            <div className="flex items-center justify-between">
              <Label className="font-manrope font-semibold">Congés & Vacances</Label>
              <Button
                type="button" size="sm" variant="outline"
                onClick={addHoliday} className="h-8"
                data-testid="cal-add-holiday-btn"
              >
                <Plus size={12} className="mr-1" /> Ajouter un congé
              </Button>
            </div>
            <p className="text-[11px] text-[#64748B]">
              Toussaint, Noël, Février, Pâques = congés courts (salaire journalier
              versé même sans pointage). <b>Grandes Vacances</b> = seuls CDD/CDI
              perçoivent leur salaire de base, sans transport ni avantages.
            </p>
            {(cal.holidays || []).length === 0 ? (
              <p className="text-[11px] italic text-[#94A3B8]">Aucun congé configuré.</p>
            ) : (
              <div className="space-y-2">
                {cal.holidays.map((h, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`cal-holiday-row-${i}`}>
                    <Select
                      value={h.kind}
                      onValueChange={(v) => setHoliday(i, 'kind', v)}
                    >
                      <SelectTrigger className="col-span-3" data-testid={`cal-holiday-kind-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {KIND_OPTIONS.map((k) => (
                          <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      className="col-span-3" placeholder="Libellé (optionnel)"
                      value={h.label || ''}
                      onChange={(e) => setHoliday(i, 'label', e.target.value)}
                    />
                    <DateRangePicker
                      from={h.start_date || ''}
                      to={h.end_date || ''}
                      onChange={({ from: f, to: t }) => setCal(prev => ({
                        ...prev,
                        holidays: prev.holidays.map((hh, j) => (j === i
                          ? { ...hh, start_date: f, end_date: t } : hh)),
                      }))}
                      testid={`cal-holiday-range-${i}`}
                      className="col-span-4 w-full"
                    />
                    <Button
                      type="button" size="sm" variant="ghost"
                      className="col-span-2 text-red-600 hover:bg-red-50"
                      onClick={() => removeHoliday(i)}
                      data-testid={`cal-holiday-remove-${i}`}
                    >
                      <Trash size={14} className="mr-1" /> Retirer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Public holidays */}
          <div className="space-y-2 border-t border-[#E2E8F0] pt-4">
            <div className="flex items-center justify-between">
              <Label className="font-manrope font-semibold">
                Jours fériés (décrets gouvernementaux)
              </Label>
              <Button
                type="button" size="sm" variant="outline"
                onClick={addPh} className="h-8"
                data-testid="cal-add-ph-btn"
              >
                <Plus size={12} className="mr-1" /> Ajouter un férié
              </Button>
            </div>
            <p className="text-[11px] text-[#64748B]">
              Chaque jour férié est crédité au salaire journalier de tous les
              employés (comme un congé court).
            </p>
            {(cal.public_holidays || []).length === 0 ? (
              <p className="text-[11px] italic text-[#94A3B8]">Aucun férié configuré.</p>
            ) : (
              <div className="space-y-2">
                {cal.public_holidays.map((p, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center"
                    data-testid={`cal-ph-row-${i}`}>
                    <DatePicker
                      value={p.date || ''}
                      onChange={(v) => setPh(i, 'date', v)}
                      testid={`cal-ph-date-${i}`}
                      className="col-span-3 w-full"
                    />
                    <Input
                      className="col-span-7"
                      placeholder="Ex: Fête Nationale, Toussaint…"
                      value={p.label || ''}
                      onChange={(e) => setPh(i, 'label', e.target.value)}
                    />
                    <Button
                      type="button" size="sm" variant="ghost"
                      className="col-span-2 text-red-600 hover:bg-red-50"
                      onClick={() => removePh(i)}
                      data-testid={`cal-ph-remove-${i}`}
                    >
                      <Trash size={14} className="mr-1" /> Retirer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* iter79/80/81 — Recompute background status indicator + progress bar + cancel */}
          {recomputeStatus?.status === 'running' && (
            <div className="px-3 py-2 rounded-md bg-blue-50 border border-blue-200 text-xs text-blue-800 space-y-2"
              data-testid="cal-recompute-running">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                Recompute des payslips en cours pour {recomputeStatus.academic_year}
                {recomputeStatus.started_at && (
                  <span className="text-blue-600/70 ml-2">
                    (démarré {new Date(recomputeStatus.started_at).toLocaleTimeString('fr-FR')})
                  </span>
                )}
                <Button
                  type="button" size="sm" variant="outline"
                  className="ml-auto h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
                  disabled={recomputeStatus.cancel_requested}
                  data-testid="cal-recompute-cancel-btn"
                  onClick={async () => {
                    try {
                      await api.post(
                        `/school-calendar/recompute-cancel?academic_year=${encodeURIComponent(recomputeStatus.academic_year)}`,
                      );
                      toast.success('Annulation demandée — arrêt au prochain payslip');
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erreur annulation');
                    }
                  }}
                >
                  {recomputeStatus.cancel_requested ? 'Annulation…' : '✕ Annuler'}
                </Button>
              </div>
              {recomputeStatus.total > 0 && (
                <>
                  <div className="w-full h-2 bg-blue-100 rounded overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-500"
                      style={{
                        width: `${Math.round(100 * (recomputeStatus.processed || 0) / recomputeStatus.total)}%`,
                      }}
                      data-testid="cal-recompute-progress-bar"
                    />
                  </div>
                  <p className="text-[10px] text-blue-700" data-testid="cal-recompute-progress-text">
                    {recomputeStatus.processed || 0} / {recomputeStatus.total} traité(s) —{' '}
                    {recomputeStatus.recomputed || 0} OK, {recomputeStatus.failed || 0} échec(s),{' '}
                    {recomputeStatus.skipped_signed || 0} signé(s) préservé(s)
                  </p>
                </>
              )}
            </div>
          )}
          {recomputeStatus?.status === 'cancelled' && (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-800"
              data-testid="cal-recompute-cancelled">
              ✕ Recompute annulé — {recomputeStatus.last_run?.processed || 0} traité(s) avant arrêt.
            </div>
          )}
          {recomputeStatus?.status === 'completed' && recomputeStatus.last_run && (
            <div className="px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-800"
              data-testid="cal-recompute-done">
              ✓ Dernier recompute : {recomputeStatus.last_run.recomputed} régénéré(s),{' '}
              {recomputeStatus.last_run.skipped_signed} signé(s) préservé(s),{' '}
              {recomputeStatus.last_run.failed || 0} échec(s)
              {recomputeStatus.completed_at && (
                <span className="text-emerald-600/70 ml-2">
                  ({new Date(recomputeStatus.completed_at).toLocaleString('fr-FR')})
                </span>
              )}
            </div>
          )}

          {/* iter76 — Public holidays CSV import */}
          <div className="border-t border-[#E2E8F0] pt-4 space-y-2">
            <Label className="font-manrope font-semibold">
              Import CSV — Fériés gouvernementaux
            </Label>
            <p className="text-[11px] text-[#64748B]">
              Format : <code>date;label</code> (délimiteur , ou ;). Ex :
              <code> 2028-01-01;Nouvel An</code>. Les dates existantes sont
              fusionnées (le libellé du CSV gagne).
            </p>
            <div className="flex gap-2 flex-wrap items-center">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm">
                <input
                  type="file" accept=".csv" className="hidden"
                  data-testid="cal-csv-import-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    const fd = new FormData();
                    fd.append('file', f);
                    fd.append('academic_year', cal.academic_year);
                    try {
                      const { data } = await api.post(
                        '/school-calendar/import-holidays-csv', fd,
                        { headers: { 'Content-Type': 'multipart/form-data' } },
                      );
                      toast.success(
                        `Import OK — ${data.added} ajout(s), ${data.updated} mise(s) à jour`,
                      );
                      load();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Erreur import CSV');
                    } finally { e.target.value = ''; }
                  }}
                />
                Choisir un fichier CSV
              </label>
              <div className="flex items-center gap-2">
                <Select value={fetchCountry} onValueChange={setFetchCountry}>
                  <SelectTrigger className="h-9 w-44" data-testid="cal-fetch-country-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button" variant="outline"
                  onClick={fetchOfficial} disabled={fetching}
                  data-testid="cal-fetch-official-btn"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  {fetching ? 'Récupération…' : '🌍 Récupérer'}
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-[#94A3B8]">
              La récupération officielle utilise date.nager.at (pays configurable
              via l'env <code>SCHOOL_COUNTRY_CODE</code>, défaut <code>CI</code>).
            </p>
          </div>

          {/* iter77 — Manual recompute trigger */}
          <div className="border-t border-[#E2E8F0] pt-4 space-y-2">
            <Label className="font-manrope font-semibold">
              Régénération manuelle des payslips
            </Label>
            <p className="text-[11px] text-[#64748B]">
              À l'enregistrement du calendrier, la régénération se déclenche
              automatiquement en arrière-plan. Utilisez ce bouton pour la
              relancer manuellement.
            </p>
            <Button
              type="button" variant="outline"
              disabled={recomputing || recomputeStatus?.status === 'running'}
              onClick={async () => {
                setRecomputing(true);
                try {
                  const { data } = await api.post(
                    `/school-calendar/recompute-payslips?academic_year=${encodeURIComponent(cal.academic_year)}`,
                  );
                  toast.success(
                    `Régénération : ${data.recomputed} OK, ${data.skipped_signed} signé(s) préservé(s), ${data.failed || 0} échec(s)`,
                  );
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'Erreur régénération');
                } finally { setRecomputing(false); }
              }}
              data-testid="cal-recompute-btn"
            >
              {recomputing || recomputeStatus?.status === 'running'
                ? 'Recompute en cours…'
                : 'Régénérer les payslips maintenant'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
